import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartSidebar from '../../components/feature/CartSidebar';
import SearchModal from '../../components/feature/SearchModal';
import LoginModal from '../../components/feature/LoginModal';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { supabase } from '../../lib/supabase';
import { SITE_ID, CURRENCY_SYMBOL } from '../../lib/constants';

interface FavoriteProduct {
  fav_id: string;
  id: string;
  urun_adi: string;
  fiyat: number;
  indirimli_fiyat: number | null;
  gorsel_url: string;
  seo_url: string;
  kategori: string;
  stok_miktari: number;
}

export default function FavoritesPage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { user } = useAuth();
  const { addItem } = useCart();

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchFavorites();
  }, [user]);

  async function fetchFavorites() {
    setLoading(true);
    const { data } = await supabase
      .from('favoriler')
      .select('id, urun_id, urunler(id, urun_adi, fiyat, indirimli_fiyat, gorsel_url, seo_url, kategori, stok_miktari)')
      .eq('site_id', SITE_ID)
      .eq('user_id', user!.id)
      .order('olusturma_tarihi', { ascending: false });

    if (data) {
      const mapped = data
        .filter((f: any) => f.urunler)
        .map((f: any) => ({
          fav_id: f.id,
          ...f.urunler,
        }));
      setFavorites(mapped);
    }
    setLoading(false);
  }

  async function removeFavorite(favId: string) {
    setRemovingId(favId);
    await supabase.from('favoriler').delete().eq('id', favId);
    setFavorites(prev => prev.filter(f => f.fav_id !== favId));
    setRemovingId(null);
  }

  function handleAddToCart(product: FavoriteProduct) {
    if (!product.stok_miktari || product.stok_miktari <= 0) return;
    const price = product.indirimli_fiyat ?? product.fiyat;
    addItem({
      id: product.id,
      product_id: product.id,
      name: product.urun_adi,
      price,
      image_url: product.gorsel_url,
      slug: product.seo_url,
      stock: product.stok_miktari,
    });
  }

  return (
    <div className="min-h-screen bg-cream-light font-sans">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Link to="/profil" className="w-9 h-9 flex items-center justify-center rounded-xl bg-white hover:bg-cream transition-colors cursor-pointer">
              <i className="ri-arrow-left-line text-coffee" />
            </Link>
            <div>
              <h1 className="font-serif text-2xl font-bold text-coffee">Favorilerim</h1>
              <p className="text-xs text-coffee-light mt-0.5">Beğendiğin ürünler</p>
            </div>
          </div>

          {!user ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-cream rounded-full mx-auto mb-4">
                <i className="ri-heart-line text-2xl text-coffee-light" />
              </div>
              <p className="text-coffee-light mb-6 text-sm">Favorilerini görmek için giriş yapman gerekiyor.</p>
              <button
                onClick={() => setLoginOpen(true)}
                className="px-6 py-3 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Giriş Yap
              </button>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-cream" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-cream rounded w-3/4" />
                    <div className="h-3 bg-cream rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : favorites.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-cream rounded-full mx-auto mb-5">
                <i className="ri-heart-line text-3xl text-coffee-light" />
              </div>
              <h2 className="font-serif text-xl font-bold text-coffee mb-2">Favori ürün yok</h2>
              <p className="text-coffee-light text-sm mb-6">Beğendiğin ürünleri favorilere ekleyerek buradan takip edebilirsin.</p>
              <Link
                to="/urunler"
                className="inline-block px-6 py-3 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap"
              >
                Ürünleri Keşfet
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {favorites.map(product => {
                const price = product.indirimli_fiyat ?? product.fiyat;
                const hasDiscount = product.indirimli_fiyat !== null && product.indirimli_fiyat < product.fiyat;

                return (
                  <div key={product.fav_id} className="bg-white rounded-2xl overflow-hidden group">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={product.gorsel_url}
                        alt={product.urun_adi}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <button
                        onClick={() => removeFavorite(product.fav_id)}
                        disabled={removingId === product.fav_id}
                        className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full hover:bg-red-50 transition-colors cursor-pointer"
                        title="Favorilerden çıkar"
                      >
                        {removingId === product.fav_id ? (
                          <i className="ri-loader-4-line animate-spin text-sm text-coffee-light" />
                        ) : (
                          <i className="ri-heart-fill text-sm text-red-500" />
                        )}
                      </button>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <p className="text-xs text-coffee-light mb-1">{product.kategori}</p>
                      <Link to={`/urun/${product.seo_url}`} className="font-semibold text-sm text-coffee hover:text-gold transition-colors line-clamp-2 block mb-3">
                        {product.urun_adi}
                      </Link>
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-1.5">
                          <span className="font-bold text-coffee">
                            {CURRENCY_SYMBOL}{price.toLocaleString('tr-TR')}
                          </span>
                          {hasDiscount && (
                            <span className="text-xs text-coffee-light line-through">
                              {CURRENCY_SYMBOL}{product.fiyat.toLocaleString('tr-TR')}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddToCart(product)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-coffee text-white text-xs font-semibold rounded-lg hover:bg-coffee-medium transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-shopping-bag-line" />
                          Sepete Ekle
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <CartSidebar />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} initialMode="login" />
    </div>
  );
}
